import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AttendanceHistory = ({ attendanceHistory, loading, onRefresh }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'clocked_in':
        return 'bg-success/10 text-success';
      case 'clocked_out':
        return 'bg-muted text-muted-foreground';
      case 'on_break':
        return 'bg-warning/10 text-warning';
      case 'absent':
        return 'bg-error/10 text-error';
      case 'late':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatHours = (hours) => {
    if (!hours) return '0.00';
    return parseFloat(hours)?.toFixed(2);
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Attendance History</h2>
          <p className="text-sm text-muted-foreground">Your recent work sessions</p>
        </div>
        <Button onClick={onRefresh} variant="outline" disabled={loading}>
          <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </Button>
      </div>

      {loading && attendanceHistory?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : attendanceHistory?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Clock" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Attendance Records</h3>
          <p className="text-muted-foreground">Your attendance history will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {attendanceHistory?.map((record) => (
            <div
              key={record?.id}
              className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-foreground">
                      {format(new Date(record?.attendance_date), 'EEEE, MMM d, yyyy')}
                    </h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(record?.status)}`}>
                      {record?.status?.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-muted-foreground mb-1 flex items-center gap-1">
                        <Icon name="LogIn" size={14} />
                        Clock In
                      </div>
                      <div className="font-medium text-foreground">
                        {record?.clock_in_time
                          ? format(new Date(record.clock_in_time), 'h:mm a')
                          : '-'}
                      </div>
                    </div>

                    <div>
                      <div className="text-muted-foreground mb-1 flex items-center gap-1">
                        <Icon name="LogOut" size={14} />
                        Clock Out
                      </div>
                      <div className="font-medium text-foreground">
                        {record?.clock_out_time
                          ? format(new Date(record.clock_out_time), 'h:mm a')
                          : '-'}
                      </div>
                    </div>

                    <div>
                      <div className="text-muted-foreground mb-1 flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        Total Hours
                      </div>
                      <div className="font-medium text-foreground">
                        {formatHours(record?.total_hours)}h
                      </div>
                    </div>

                    <div>
                      <div className="text-muted-foreground mb-1 flex items-center gap-1">
                        <Icon name="Coffee" size={14} />
                        Break
                      </div>
                      <div className="font-medium text-foreground">
                        {record?.break_duration_minutes || 0}m
                      </div>
                    </div>
                  </div>

                  {(record?.regular_hours > 0 || record?.overtime_hours > 0) && (
                    <div className="mt-3 pt-3 border-t border-border flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1.5">
                        <Icon name="Clock" size={14} className="text-primary" />
                        <span className="text-muted-foreground">Regular:</span>
                        <span className="font-medium text-foreground">
                          {formatHours(record?.regular_hours)}h
                        </span>
                      </div>
                      {record?.overtime_hours > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Icon name="TrendingUp" size={14} className="text-warning" />
                          <span className="text-muted-foreground">Overtime:</span>
                          <span className="font-medium text-warning">
                            {formatHours(record?.overtime_hours)}h
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="text-right ml-4">
                  <div className="text-2xl font-bold text-primary">
                    {formatHours(record?.total_hours)}
                  </div>
                  <div className="text-xs text-muted-foreground">hours</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {attendanceHistory?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                {attendanceHistory?.length}
              </div>
              <div className="text-xs text-muted-foreground">Days Worked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                {formatHours(
                  attendanceHistory?.reduce((sum, r) => sum + (parseFloat(r?.total_hours) || 0), 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Total Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                {formatHours(
                  attendanceHistory?.reduce((sum, r) => sum + (parseFloat(r?.regular_hours) || 0), 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Regular Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning mb-1">
                {formatHours(
                  attendanceHistory?.reduce((sum, r) => sum + (parseFloat(r?.overtime_hours) || 0), 0)
                )}
              </div>
              <div className="text-xs text-muted-foreground">Overtime Hours</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceHistory;